// Short links (D354, docs/chat-essentials.md): a URL in a message is shown as
// its host ("amazon.in"), and opens the full URL. Display only: the stored,
// sealed text is unchanged, and Copy and search use the full text.

/** http(s) URLs in message text; the same shape `firstUrl` looks for. */
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;

const OPENERS: Record<string, string | undefined> = {
  ')': '(',
  ']': '[',
  '}': '{',
};

/**
 * Trailing punctuation that ends the sentence rather than the URL. A closing
 * bracket stays when the URL opened one ("…/Rang_(film)").
 */
export function trimUrl(raw: string): string {
  let url = raw;
  for (;;) {
    const last = url[url.length - 1];
    if (!last) return url;
    if ('.,;:!?\'"'.includes(last)) {
      url = url.slice(0, -1);
      continue;
    }
    const open = OPENERS[last];
    if (open && count(url, open) < count(url, last)) {
      url = url.slice(0, -1);
      continue;
    }
    return url;
  }
}

function count(text: string, char: string): number {
  return text.split(char).length - 1;
}

/**
 * The host to show for `url`, without a leading "www.", or null when it isn't
 * an http(s) URL the parser accepts. Always the parser's host, never a piece
 * of the text, so "https://amazon.in.evil.com/…" shows "amazon.in.evil.com"
 * and "https://amazon.in@evil.com" shows "evil.com".
 */
export function linkHost(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  // The browser gives an internationalised domain in its xn-- form; we show
  // that rather than decode it, which also keeps look-alike letters visible.
  const host = parsed.hostname.replace(/^www\./i, '');
  return host || null;
}

export interface LinkSegment {
  text: string;
  /** Set on a link: the full URL it opens. `text` is then its host. */
  url?: string;
}

/** Splits message text into plain text and short links. */
export function splitLinks(text: string): LinkSegment[] {
  const segments: LinkSegment[] = [];
  let plain = '';
  let last = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const url = trimUrl(match[0]);
    const host = linkHost(url);
    const start = match.index!;
    plain += text.slice(last, start);
    last = start;
    if (!host) continue; // left as text, as before
    if (plain) segments.push({ text: plain });
    plain = '';
    segments.push({ text: host, url });
    last = start + url.length;
  }
  plain += text.slice(last);
  if (plain) segments.push({ text: plain });
  return segments;
}

/** The text with every URL replaced by its host, for one-line quotes. */
export function shortenLinks(text: string): string {
  return splitLinks(text)
    .map((segment) => segment.text)
    .join('');
}
